import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, ChevronDown, ChevronUp, User, Clock, RefreshCw } from 'lucide-react';
import { getCommunityPosts, getCommunityReplies, submitCommunityPost, getAccessToken, getUserInfo } from '../../services/api';
import AlertModal, { useAlert } from '../Common/Alert';
import './CommunityPage.css';

const CommunityPage = () => {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [repliesLoading, setRepliesLoading] = useState({});
  const [repliesData, setRepliesData] = useState({});
  const [currentUsername, setCurrentUsername] = useState('');
  
  const { alert, showAlert, closeAlert, showAuthAlert } = useAlert();
  const sentinelRef = useRef(null);
  const boardRef = useRef(null);

  // 사용자 정보 가져오기
  const fetchUserInfo = async () => {
    try {
      const userInfo = await getUserInfo();
      setCurrentUsername(userInfo.userNickname);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setCurrentUsername('익명사용자');
    }
  };
  // API에서 커뮤니티 데이터 가져오기
  const fetchCommunityPosts = async (cursor = null, append = false) => {
    try {
      setLoading(true);
      const data = await getCommunityPosts(cursor);
      
      // API 응답 데이터를 컴포넌트에서 사용하는 형태로 변환
      const transformedPosts = data.items.map(item => ({
        id: item.communityNo.toString(),
        userId: item.author,
        content: item.content,
        timestamp: new Date(item.createdAt),
        replyCount: item.replyCommentCount || 0,
        replies: [] // 댓글은 별도 API로 가져옴
      }));
      
      if (append) {
        setPosts(prevPosts => [...prevPosts, ...transformedPosts]);
      } else {
        setPosts(transformedPosts);
      }
      
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
      
    } catch (error) {
      console.error('Error fetching community posts:', error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchUserInfo();
    fetchCommunityPosts();
  }, []);

  // IntersectionObserver로 무한 스크롤 감지 (Wrap이 scroll container이므로 window scroll 사용 불가)
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextCursor && hasMore && !loading && !initialLoading) {
          fetchCommunityPosts(nextCursor, true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [nextCursor, hasMore, loading, initialLoading]);

  // 시간 포맷팅
  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (hours > 24) {
      return timestamp.toLocaleDateString('ko-KR', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      });
    } else if (hours > 0) {
      return `${hours}시간 전`;
    } else if (minutes > 0) {
      return `${minutes}분 전`;
    } else {
      return '방금 전';
    }
  };

  // API에서 댓글 데이터 가져오기
  const fetchReplies = async (communityNo) => {
    try {
      setRepliesLoading(prev => ({ ...prev, [communityNo]: true }));
      
      const data = await getCommunityReplies(communityNo);
      
      // 댓글 데이터를 컴포넌트에서 사용하는 형태로 변환
      const transformedReplies = data.list.map(reply => ({
        id: reply.communityNo.toString(),
        userId: reply.author,
        content: reply.content,
        timestamp: new Date(reply.createdAt)
      }));
      
      // 해당 게시글의 댓글 데이터 저장
      setRepliesData(prev => ({
        ...prev,
        [communityNo]: transformedReplies
      }));
      
      // posts 상태도 업데이트하여 댓글 수 반영
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === communityNo.toString() 
            ? { ...post, replies: transformedReplies }
            : post
        )
      );
      
    } catch (error) {
      console.error('Error fetching replies:', error);
    } finally {
      setRepliesLoading(prev => ({ ...prev, [communityNo]: false }));
    }
  };

  // 스크롤 컨테이너 찾기 (Wrap: position fixed + overflowY auto)
  const getScrollContainer = () => {
    let el = boardRef.current?.parentElement;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
      el = el.parentElement;
    }
    return window;
  };

  // API로 게시글 작성
  const submitPost = async (content, parentCommunityNo = null) => {
    try {
      const token = getAccessToken();
      if (!token) {
        showAuthAlert();
        return false;
      }

      await submitCommunityPost(content, parentCommunityNo);
      // showAlert('success', '게시글이 성공적으로 작성되었습니다.');
      return true;
    } catch (error) {
      console.error('Error submitting post:', error);
      showAlert('error', '게시글 작성에 실패했습니다.');
      return false;
    }
  };

  // 게시글/댓글 통합 작성 핸들러
  const handleSubmit = async (e, postId = null) => {
    e.preventDefault();

    // 게시글인지 댓글인지에 따라 텍스트 확인
    const content = postId ? replyText : newPost;
    if (!content.trim()) return;

    // 로딩 상태 설정
    if (postId) {
      setRepliesLoading(prev => ({ ...prev, [postId]: true }));
    } else {
      setLoading(true);
    }

    // API 호출
    const success = await submitPost(content, postId ? parseInt(postId) : null);

    if (success) {
      // 입력 필드 초기화
      if (postId) {
        setReplyText('');
        setReplyingTo(null);

        // 댓글 목록 새로고침
        await fetchReplies(parseInt(postId));

        // 댓글 목록을 자동으로 펼쳐서 방금 작성한 댓글이 보이도록 함
        setExpandedPosts(prev => {
          const newSet = new Set(prev);
          newSet.add(postId);
          return newSet;
        });
      } else {
        setNewPost('');

        // 게시글 목록 새로고침
        await fetchCommunityPosts();

        // 페이지 맨 위로 스크롤
        getScrollContainer().scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    // 로딩 상태 해제
    if (postId) {
      setRepliesLoading(prev => ({ ...prev, [postId]: false }));
    } else {
      setLoading(false);
    }
  };

  // 댓글 토글 (댓글 목록 + 답글 작성 폼 동시 표시)
  const toggleReplies = async (postId) => {
    const isExpanded = expandedPosts.has(postId);

    if (isExpanded) {
      // 닫을 때: 댓글 목록과 답글 작성 폼 모두 닫기
      setExpandedPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
      setReplyingTo(null);
      setReplyText('');
    } else {
      // 열 때: 댓글 목록과 답글 작성 폼 모두 열기
      setExpandedPosts(prev => {
        const newSet = new Set(prev);
        newSet.add(postId);
        return newSet;
      });
      setReplyingTo(postId);

      // 댓글을 처음 펼칠 때만 API 호출
      if (!repliesData[postId]) {
        fetchReplies(parseInt(postId));
      }
    }
  };

  return (
    <div className="community-board" ref={boardRef}>
      {/* 상단 헤더 */}
      <div className="far-header">
        <div className="far-header-left">
          <span className="far-header-report-type">COMMUNITY</span>
          <div className="far-header-title-row">
            <span className="far-header-title">투자 커뮤니티</span>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="feature-section">
            <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <MessageSquare size={24} />
                </div>
                <h2 className="board-title">공지</h2>
            </div>
            <p className="board-subtitle">투자자들과 정보를 공유하거나 개발자에게 추가하고 싶은 종목, 기능들을 자유롭게 건의해보세요.</p>
            <p className="board-subtitle">현재 AI 모델 고도화 작업 중에 있습니다. 점수가 하루에 여러번 업데이트 될 수 있으니 참고 바랍니다.</p>
        </div>

        {/* 새 게시글 작성 */}
        <div className="post-form-container">
        <form onSubmit={(e) => handleSubmit(e)} className="post-form">
          <div className="form-header">
            <div className="user-avatar">
              <User size={16} />
            </div>
            <span className="current-user">{currentUsername}</span>
          </div>
          <textarea
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            placeholder="궁금한 점이나 의견을 작성해주세요."
            className="post-textarea"
            rows="3"
          />
          <div className="form-actions">
            <div className="char-count">
              {newPost.length}/500
            </div>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={!newPost.trim() || newPost.length > 500 || loading}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  작성 중...
                </>
              ) : (
                <>
                  <Send size={16} />
                  작성하기
                </>
              )}
            </button>
          </div>
        </form>
        </div>

        {/* 게시글 목록 */}
        <div className="posts-container">
        {initialLoading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="loading-icon animate-spin" />
            <p>게시글을 불러오는 중...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} className="empty-icon" />
            <h3>아직 게시글이 없습니다</h3>
            <p>첫 번째 게시글을 작성해보세요!</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="post-card">
              <div className="post-header">
                <div className="post-user">
                  <div className="user-avatar">
                    <User size={16} />
                  </div>
                  <span className="username">{post.userId}</span>
                </div>
                <div className="post-time">
                  <Clock size={12} />
                  {formatTime(post.timestamp)}
                </div>
              </div>

              <div className="post-content">
                {post.content}
              </div>

              <div className="post-actions">
                <button
                  className="reply-toggle-btn"
                  onClick={() => toggleReplies(post.id)}
                  disabled={repliesLoading[post.id]}
                >
                  <MessageSquare size={16} />
                  {repliesLoading[post.id] ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      로딩 중...
                    </>
                  ) : (
                    <>
                      댓글 {post.replyCount || 0}개
                      {expandedPosts.has(post.id) ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </>
                  )}
                </button>
              </div>

              {/* 댓글 영역 (댓글 목록 + 댓글 작성 폼) */}
              {expandedPosts.has(post.id) && (
                <>
                  {/* 댓글 작성 폼 */}
                  <div className="reply-form-container">
                    <form onSubmit={(e) => handleSubmit(e, post.id)} className="reply-form">
                      <div className="form-header">
                        <div className="user-avatar small">
                          <User size={12} />
                        </div>
                        <span className="current-user">{currentUsername}</span>
                      </div>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="댓글을 작성해주세요..."
                        className="reply-textarea"
                        rows="2"
                      />
                      <div className="form-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                          className="cancel-btn"
                        >
                          취소
                        </button>
                        <button
                          type="submit"
                          className="submit-btn small"
                          disabled={!replyText.trim() || repliesLoading[post.id]}
                        >
                          {repliesLoading[post.id] ? (
                            <>
                              <RefreshCw size={14} className="animate-spin" />
                              작성 중...
                            </>
                          ) : (
                            <>
                              <Send size={14} />
                              댓글 작성
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* 댓글 목록 */}
                  <div className="replies-container">
                    {repliesLoading[post.id] ? (
                      <div className="replies-loading">
                        <RefreshCw size={20} className="animate-spin" />
                        <span>댓글을 불러오는 중...</span>
                      </div>
                    ) : repliesData[post.id] && repliesData[post.id].length > 0 ? (
                      repliesData[post.id].map((reply) => (
                        <div key={reply.id} className="reply-card">
                          <div className="reply-header">
                            <div className="reply-user">
                              <div className="user-avatar small">
                                <User size={12} />
                              </div>
                              <span className="username">{reply.userId}</span>
                            </div>
                            <div className="reply-time">
                              {formatTime(reply.timestamp)}
                            </div>
                          </div>
                          <div className="reply-content">
                            {reply.content}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-replies">
                        <p>첫 번째 댓글을 작성해보세요!</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        )}

        {/* 무한 스크롤 로딩 인디케이터 */}
        {!initialLoading && loading && hasMore && (
          <div className="infinite-scroll-loading">
            <RefreshCw size={20} className="animate-spin" />
            <span>새로운 게시글을 불러오는 중...</span>
          </div>
        )}
        
        {!initialLoading && !hasMore && posts.length > 0 && (
          <div className="end-message">
            <p>모든 게시글을 확인했습니다.</p>
          </div>
        )}

        {/* IntersectionObserver 감지용 sentinel */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
      </div>
      
      <AlertModal alert={alert} onClose={closeAlert} />
    </div>
  );
};

export default CommunityPage;